import {
  Alert,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useRestoreVersion, useVersions } from '../../api/queries/versions';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  bookId: string;
}

export function VersionHistoryDrawer({ open, onClose, bookId }: Props) {
  const { data, isLoading } = useVersions(bookId);
  const restore = useRestoreVersion(bookId);
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
  const [restoreConfirmId, setRestoreConfirmId] = useState<string | null>(null);

  const handleRestore = (versionId: string) => {
    setRestoreConfirmId(versionId);
  };

  const handleRestoreConfirm = async () => {
    if (!restoreConfirmId) return;
    setPendingRestoreId(restoreConfirmId);
    setRestoreConfirmId(null);
    try {
      await restore.mutateAsync(restoreConfirmId);
    } finally {
      setPendingRestoreId(null);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, p: 2 } }}
    >
      <Stack spacing={2}>
        <Typography variant="h6">Version history</Typography>
        <Typography variant="caption" color="text.secondary">
          Snapshots capture book content on update (at most once per minute).
          Restoring overwrites current content but first saves a safety snapshot
          so you can undo.
        </Typography>

        {isLoading && <Alert severity="info">Loading…</Alert>}
        {data?.length === 0 && (
          <Alert severity="info">
            No snapshots yet. Edit the book to produce one.
          </Alert>
        )}

        <List>
          {(data ?? []).map((v) => (
            <ListItem
              key={v.id}
              divider
              secondaryAction={
                <Button
                  size="small"
                  onClick={() => handleRestore(v.id)}
                  disabled={restore.isPending && pendingRestoreId === v.id}
                >
                  {restore.isPending && pendingRestoreId === v.id
                    ? 'Restoring…'
                    : 'Restore'}
                </Button>
              }
            >
              <ListItemText
                primary={new Date(v.createdAt).toLocaleString()}
                secondary={v.id.slice(0, 8)}
              />
            </ListItem>
          ))}
        </List>
      </Stack>

      <ConfirmDialog
        open={restoreConfirmId !== null}
        title="Restore snapshot"
        message="Restore this snapshot? A safety snapshot of the current state will be saved first."
        confirmLabel="Restore"
        confirmColor="primary"
        onConfirm={handleRestoreConfirm}
        onCancel={() => setRestoreConfirmId(null)}
      />
    </Drawer>
  );
}
